import { Fragment, useMemo, useState } from 'react';
import { Alert } from '../../../components/ui/Alert';
import { LoadingState } from '../../../components/ui/States';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Modal } from '../../../components/ui/Modal';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import { isConstant, Nil, Table, THead, Th, Td, Tr } from '../../../components/ui/Table';
import {
  useAddPilotPeople,
  useAssignPeople,
  useCompanySearch,
  useCreatePilot,
  useRemovePilot,
  useRemovePilotPerson,
  useSetCompanyLanguage,
  useReps,
  useSuggestions,
  useUpdatePilotPerson,
} from '../../../hooks/usePilots';
import { useAuth } from '../../../hooks/useAuth';
import type { ChoosableLanguage, CompanyOption, Flag, Language, NewPilotPerson, PersonSuggestion, PilotDetail, PilotPerson, Rep } from '../../../api/pilots';
import { CHOOSABLE_LANGUAGES, LANGUAGES } from '../../../api/pilots';
import { formatDateTime, formatRelative } from '../outreach/format';
import { plural } from '../../ProjectManagement/formatters';
import { ImportListModal } from './ImportListModal';
import { ACTIVITY_LABELS, LANGUAGE_META, LanguageBadge, PersonName, STAGE_META, StageBadge, flagText, repLabel, repName } from './format';
import { MoreMenu, SidePanel, selectClass } from './parts';

export const inputClass =
  'w-full rounded-md border border-border-light bg-surface-primary px-2.5 py-1.5 text-body-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-accent';

function RepSelect({ reps, value, onChange, placeholder = 'Unassigned' }: { reps: Rep[]; value: string; onChange: (email: string) => void; placeholder?: string }) {
  return (
    <select className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {reps.map((r) => <option key={r.email} value={r.email}>{repLabel(r)}</option>)}
    </select>
  );
}

// ── Adding a company ────────────────────────────────────────────────────────

function AddCompanyModal({ bucketId, isOpen, onClose }: { bucketId: string; isOpen: boolean; onClose: () => void }) {
  const [q, setQ] = useState('');
  const search = useCompanySearch(q);
  const create = useCreatePilot(bucketId);
  const add = (company: CompanyOption) =>
    create.mutate({ companyName: company.name, companyDomain: company.domain, attioCompanyId: company.attioCompanyId }, { onSuccess: () => { setQ(''); onClose(); } });
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add a company" description="Pick the account from Attio, then add its people." size="lg">
      <div className="space-y-3">
        <input autoFocus className={inputClass} placeholder="Company name, e.g. Sodexo" value={q} onChange={(e) => setQ(e.target.value)} />
        {search.error && <Alert variant="error">Attio search failed: {search.error.message}</Alert>}
        {create.error && <Alert variant="error">Could not add the company: {create.error.message}</Alert>}
        {search.isFetching && <LoadingState rows={2} label="Searching Attio…" />}
        {search.data && search.data.companies.length === 0 && (
          <p className="text-body-sm text-text-tertiary">No Attio company matches “{q}”. Check the spelling, or create it in Attio first.</p>
        )}
        <ul className="divide-y divide-border-light">
          {search.data?.companies.map((c) => (
            <li key={c.attioCompanyId} className="flex items-center justify-between gap-3 py-2">
              <div>
                <div className="text-body-sm font-medium text-text-primary">{c.name}</div>
                <div className="text-body-xs text-text-tertiary">{c.domain ?? 'no domain in Attio'}</div>
              </div>
              <Button size="sm" variant="secondary" isLoading={create.isPending} onClick={() => add(c)}>Add</Button>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

// ── Adding people to a company ──────────────────────────────────────────────

function AddPeopleModal({ company, isOpen, onClose, reps }: { company: PilotDetail; isOpen: boolean; onClose: () => void; reps: Rep[] }) {
  const suggestions = useSuggestions(company.id, isOpen);
  const add = useAddPilotPeople(company.id);
  const repEmails = useMemo(() => new Set(reps.map((r) => r.email)), [reps]);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [manual, setManual] = useState({ fullName: '', jobTitle: '', linkedinUrl: '', email: '', assigneeEmail: '' });
  const [bulkRep, setBulkRep] = useState('');

  const keyOf = (s: PersonSuggestion) => s.linkedinUrl ?? s.email ?? s.fullName;
  const open = (suggestions.data?.suggestions ?? []).filter((s) => !s.alreadyInPilot);
  const selected = open.filter((s) => keyOf(s) in picked);
  const toggle = (s: PersonSuggestion) =>
    setPicked((prev) => {
      const next = { ...prev };
      const k = keyOf(s);
      if (k in next) delete next[k];
      else next[k] = s.currentSender && repEmails.has(s.currentSender) ? s.currentSender : bulkRep;
      return next;
    });
  const ready: NewPilotPerson[] = selected.map((s) => ({ fullName: s.fullName, jobTitle: s.jobTitle, linkedinUrl: s.linkedinUrl, email: s.email, assigneeEmail: picked[keyOf(s)] || null }));
  const manualReady = manual.fullName.trim() && (manual.linkedinUrl.trim() || manual.email.trim());
  const submit = () =>
    add.mutate([...ready, ...(manualReady ? [{ ...manual, email: manual.email.trim() || null, assigneeEmail: manual.assigneeEmail || null }] : [])], {
      onSuccess: () => { setPicked({}); setManual({ fullName: '', jobTitle: '', linkedinUrl: '', email: '', assigneeEmail: '' }); },
    });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Add people · ${company.companyName}`} description="Tick people. A rep is optional here: people already in lemlist start on the rep already sending to them, the rest you split on the bucket page." size="full">
      <div className="space-y-4">
        {suggestions.isLoading && <LoadingState rows={2} />}
        {suggestions.error && <Alert variant="error">Could not load suggestions: {suggestions.error.message}</Alert>}
        {suggestions.data?.attioError && <Alert variant="warning">Attio people could not be loaded, so this list is incomplete: {suggestions.data.attioError}</Alert>}
        {open.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-body-xs text-text-tertiary">Give everyone ticked without a rep:</span>
              <div className="w-48"><RepSelect reps={reps} value={bulkRep} placeholder="Nobody yet" onChange={(email) => { setBulkRep(email); setPicked((prev) => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, v || email]))); }} /></div>
            </div>
            <div className="max-h-[22rem] overflow-y-auto rounded-md border border-border-light">
              <Table minWidth={760}>
                <THead sticky><tr><Th /><Th>Person</Th><Th>Known from</Th><Th>In lemlist</Th><Th>Rep</Th></tr></THead>
                <tbody>
                  {open.map((s) => {
                    const k = keyOf(s);
                    const on = k in picked;
                    return (
                      <Tr key={k}>
                        <Td><input type="checkbox" checked={on} onChange={() => toggle(s)} /></Td>
                        <Td>
                          <div className="font-medium text-text-primary">{s.fullName}</div>
                          <div className="text-body-xs text-text-tertiary">{s.jobTitle ?? 'No job title'}</div>
                        </Td>
                        <Td><span className="text-body-xs text-text-secondary">{s.sources.join(' + ')}</span></Td>
                        <Td><span className="text-body-xs text-text-tertiary">{s.campaigns.length > 0 ? `${s.campaigns.join(', ')}${s.currentSender ? ` · ${s.currentSender.split('@')[0]}` : ''}` : <Nil />}</span></Td>
                        <Td>{on && <div className="w-44"><RepSelect reps={reps} value={picked[k] ?? ''} onChange={(email) => setPicked((p) => ({ ...p, [k]: email }))} /></div>}</Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </>
        )}
        {suggestions.data && open.length === 0 && <p className="text-body-sm text-text-tertiary">Nobody new at this account in lemlist or Attio. Add someone by LinkedIn link below.</p>}
        <div className="space-y-2">
          <p className="text-body-xs font-medium text-text-secondary">Someone else (LinkedIn link or email needed, so lemlist can be matched)</p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
            <input className={inputClass} placeholder="Full name" value={manual.fullName} onChange={(e) => setManual({ ...manual, fullName: e.target.value })} />
            <input className={inputClass} placeholder="Job title" value={manual.jobTitle} onChange={(e) => setManual({ ...manual, jobTitle: e.target.value })} />
            <input className={inputClass} placeholder="linkedin.com/in/…" value={manual.linkedinUrl} onChange={(e) => setManual({ ...manual, linkedinUrl: e.target.value })} />
            <input className={inputClass} placeholder="Email (optional)" value={manual.email} onChange={(e) => setManual({ ...manual, email: e.target.value })} />
            <RepSelect reps={reps} value={manual.assigneeEmail} placeholder="Nobody yet" onChange={(email) => setManual({ ...manual, assigneeEmail: email })} />
          </div>
        </div>
        {add.error && <Alert variant="error">Not added: {add.error.message}</Alert>}
        {add.data && (
          <Alert variant={add.data.alreadyInPilot.length + add.data.noIdentifier.length > 0 ? 'warning' : 'success'}>
            Added {add.data.added}.
            {add.data.alreadyInPilot.length > 0 && ` Already here: ${add.data.alreadyInPilot.join(', ')}.`}
            {add.data.noIdentifier.length > 0 && ` Skipped, no LinkedIn link or email: ${add.data.noIdentifier.join(', ')}.`}
          </Alert>
        )}
        <div className="flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>Done</Button>
          <Button onClick={submit} isLoading={add.isPending} disabled={ready.length === 0 && !manualReady}>Add {ready.length + (manualReady ? 1 : 0)}</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── One person: a slim row, everything else in the side panel ──────────────

function Touches({ person }: { person: PilotPerson }) {
  const t = person.touches;
  const items: [string, number | string][] = [
    ['LinkedIn invites', `${t.linkedinInvites}${t.linkedinAccepted ? ' · accepted' : ''}`],
    ['LinkedIn messages', t.linkedinMessages],
    ['Emails', t.emails],
    ['Calls in lemlist', t.calls],
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-body-xs">
      {items.map(([label, value]) => (
        <Fragment key={label}><dt className="text-text-tertiary">{label}</dt><dd className="tabular-nums text-text-primary">{value}</dd></Fragment>
      ))}
    </dl>
  );
}

function PersonPanel({ entry, isAdmin, reps, onClose }: { entry: Entry; isAdmin: boolean; reps: Rep[]; onClose: () => void }) {
  const { company, person } = entry;
  const update = useUpdatePilotPerson(company.id);
  const remove = useRemovePilotPerson(company.id);
  const error = update.error ?? remove.error;
  const tickTitle = (at: string | null, by: string | null, fallback: string) => (at ? `Ticked by ${by} ${formatDateTime(at)}` : fallback);
  const section = (title: string, body: React.ReactNode) => (
    <section className="space-y-2"><h4 className="text-body-xs font-medium uppercase tracking-wide text-text-tertiary">{title}</h4>{body}</section>
  );
  return (
    <SidePanel
      isOpen
      onClose={onClose}
      title={
        <div>
          <div className="flex items-center gap-2 text-body-md font-medium text-text-primary"><LanguageBadge language={person.language} /><PersonName fullName={person.fullName} companyName={company.companyName} /></div>
          <div className="text-body-xs text-text-tertiary">{[person.jobTitle, company.companyName, person.country].filter(Boolean).join(' · ')}</div>
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-3 text-body-sm">
        {person.linkedinUrl && <a href={person.linkedinUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline">LinkedIn ↗</a>}
        {person.email && <span className="text-text-secondary">{person.email}</span>}
      </div>
      {person.flags.length > 0 && (
        <div className="space-y-1 rounded-md bg-warning-light/60 px-3 py-2">
          {person.flags.map((f) => <div key={f.kind} className="text-body-xs text-warning-dark">{flagText(f)}</div>)}
        </div>
      )}
      {section('Rep', isAdmin
        ? <RepSelect reps={reps} value={person.assignee?.email ?? ''} onChange={(email) => update.mutate({ personId: person.id, assigneeEmail: email || null })} />
        : <span className="text-body-sm text-text-primary">{repName(person.assignee)}</span>)}
      {section('Progress', (
        <div className="space-y-3">
          <StageBadge stage={person.stage} />
          <Touches person={person} />
          <div className="flex gap-4">
            <ManualTick label="Called outside lemlist" checked={person.touches.manualCall} disabled={update.isPending} title={tickTitle(person.manualCallAt, person.manualCallBy, 'A call made outside lemlist')} onChange={(v) => update.mutate({ personId: person.id, manualCall: v })} />
            <ManualTick label="Meeting held" checked={person.touches.manualMeeting} disabled={update.isPending} title={tickTitle(person.manualMeetingAt, person.manualMeetingBy, 'A meeting that is in no synced calendar')} onChange={(v) => update.mutate({ personId: person.id, manualMeeting: v })} />
          </div>
          {person.campaigns.length > 0 && <div className="text-body-xs text-text-tertiary">In {person.campaigns.join(', ')}</div>}
        </div>
      ))}
      {section('Timeline', person.timeline.length === 0 ? (
        <p className="text-body-xs text-text-muted">No lemlist activity yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {person.timeline.map((e, i) => (
            <li key={`${e.at}-${i}`} className="text-body-xs text-text-secondary">
              <span className="tabular-nums text-text-tertiary">{formatDateTime(e.at)}</span>{' · '}
              <span className="font-medium">{ACTIVITY_LABELS[e.type] ?? e.type}</span>
              {e.sender && ` · ${e.sender}`}
              {e.detail && <div className="ml-4 mt-0.5 whitespace-pre-wrap text-text-tertiary">{e.detail}</div>}
            </li>
          ))}
        </ul>
      ))}
      {error && <Alert variant="error">Not saved: {error.message}</Alert>}
      {isAdmin && (
        <button
          className="text-body-xs text-text-muted hover:text-error"
          onClick={() => { if (window.confirm(`Remove ${person.fullName} from this bucket? Nothing changes in lemlist.`)) remove.mutate(person.id, { onSuccess: onClose }); }}
        >
          Remove from bucket
        </button>
      )}
    </SidePanel>
  );
}

function ManualTick({ label, checked, title, disabled, onChange }: { label: string; checked: boolean; title: string; disabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="inline-flex items-center gap-1.5 text-body-xs text-text-secondary" title={title}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

/**
 * Which columns carry information: a column with one value for everyone is said
 * once above the table. Values shared by most rows (the usual language, a flag
 * everyone has) are said once too, and the rows only mark the exceptions.
 */
interface Cols {
  other: boolean;
  stage: boolean;
  /** False when nobody shown has been touched yet: the Last touch column goes. */
  lastTouch: boolean;
  /** Flags that are on every row shown, said once above the table rather than as a ⚠ per row. */
  commonFlags: ReadonlySet<Flag['kind']>;
  /** False when no row has a flag beyond the common ones: the ⚠ column goes. */
  flags: boolean;
  /** The language most people shown have: its badge is left off, every other language keeps one. */
  usualLanguage: Language | null;
  span: number;
}

/** "Not started" is the default state: nothing. Every other stage is an exception and keeps its badge. */
function StageCell({ stage }: { stage: PilotPerson['stage'] }) {
  if (stage === 'not_started') return null;
  return <StageBadge stage={stage} />;
}

interface RowProps {
  entry: Entry;
  isAdmin: boolean;
  cols: Cols;
  /** The column that is not already the group: the company when grouped by rep, the rep when grouped by company. */
  view: View;
  selected: boolean;
  onSelect: (on: boolean) => void;
  onOpen: () => void;
  /** Inside a company group, the group's own language: a badge only marks people who differ from it. */
  groupLanguage?: Language | null;
}

function PersonRow({ entry: { company, person }, isAdmin, cols, view, selected, onSelect, onOpen, groupLanguage }: RowProps) {
  const baseline = groupLanguage ?? cols.usualLanguage;
  return (
    <Tr>
      {isAdmin && <Td><input type="checkbox" checked={selected} onChange={(e) => onSelect(e.target.checked)} aria-label={`Select ${person.fullName}`} /></Td>}
      <Td>
        <button onClick={onOpen} className="text-left">
          <div className="flex items-center gap-1.5 font-medium text-text-primary hover:underline">
            {/* "Pick Dutch or French" is a decision, so it always shows. */}
            {(person.language !== baseline || person.language === 'nl_fr') && <LanguageBadge language={person.language} />}
            <PersonName fullName={person.fullName} companyName={company.companyName} />
            {/* The stage sits beside the name, only when it is not "not started": a Stage column was blank on
                every row of a page when only 3 of 266 people had started. */}
            <StageCell stage={person.stage} />
          </div>
          <div className="break-words text-body-xs text-text-tertiary">{person.jobTitle ?? 'No job title'}</div>
        </button>
      </Td>
      {cols.other && (
        <Td>
          {view === 'rep'
            ? <span className="text-body-sm text-text-secondary">{company.companyName}</span>
            : person.assignee ? <span className="text-body-sm text-text-secondary">{repName(person.assignee)}</span> : <Nil />}
        </Td>
      )}
      {cols.lastTouch && (
        <Td><span className="text-body-xs text-text-tertiary">{person.lastTouchAt ? formatRelative(person.lastTouchAt) : <Nil />}</span></Td>
      )}
      {cols.flags && (
        <Td>
          {person.flags.some((f) => !cols.commonFlags.has(f.kind)) && (
            <span className="cursor-help text-warning-dark" title={person.flags.map(flagText).join('\n')}>⚠</span>
          )}
        </Td>
      )}
    </Tr>
  );
}

// ── Groups: by company, or by rep ───────────────────────────────────────────

/** "1 person", "12 people". */
function peopleCount(n: number): string {
  return plural(n, 'person', 'people');
}

/** The language most of these people have, or null when there is nobody. */
function mostCommonLanguage(persons: readonly PilotPerson[]): Language | null {
  const counts = new Map<Language, number>();
  for (const p of persons) counts.set(p.language, (counts.get(p.language) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

interface Entry { company: PilotDetail; person: PilotPerson }

/** The collapsible header row shared by both views; `extra` is what differs. */
function GroupHeader({ title, ids, isAdmin, colSpan, selection, collapsed, onToggle, extra, note }: {
  title: string; ids: string[]; isAdmin: boolean; colSpan: number; selection: Selection;
  collapsed: boolean; onToggle: () => void; extra?: React.ReactNode; note: string;
}) {
  const allOn = ids.length > 0 && ids.every((id) => selection.has(id));
  return (
    <tr className="bg-surface-secondary">
      <td colSpan={colSpan} className="px-3 py-1.5">
        <div className="flex flex-wrap items-center gap-3">
          {isAdmin && <input type="checkbox" checked={allOn} disabled={ids.length === 0} onChange={(e) => selection.setMany(ids, e.target.checked)} aria-label={`Select everyone in ${title}`} />}
          <button onClick={onToggle} className="text-body-sm font-medium text-text-primary hover:underline">{collapsed ? '▸' : '▾'} {title}</button>
          <span className="text-body-xs text-text-tertiary">{note}</span>
          {extra && <span className="ml-auto flex items-center gap-2">{extra}</span>}
        </div>
      </td>
    </tr>
  );
}

/** Only where it is a decision: a Belgian company still waiting for Dutch or French, or one already picked by hand. */
function CompanyLanguage({ company }: { company: PilotDetail }) {
  const set = useSetCompanyLanguage(company.id);
  const undecided = !company.languageOverride && (company.language === 'nl_fr' || company.persons.some((p) => p.language === 'nl_fr'));
  if (!undecided && !company.languageOverride) return null;
  return (
    <>
      <select
        className={`${selectClass} ${undecided ? 'border-warning text-warning-dark' : ''}`}
        value={company.languageOverride ?? ''}
        disabled={set.isPending}
        onChange={(e) => set.mutate((e.target.value || null) as ChoosableLanguage | null)}
      >
        <option value="">{undecided ? 'Pick Dutch or French' : 'By location'}</option>
        {CHOOSABLE_LANGUAGES.map((l) => <option key={l} value={l}>{LANGUAGE_META[l].label}</option>)}
      </select>
      {set.error && <span className="text-body-xs text-error">Not saved: {set.error.message}</span>}
    </>
  );
}

function CompanyGroup({ company, persons, rows = persons, isAdmin, cols, reps, selection, onOpen }: { company: PilotDetail; persons: PilotPerson[]; /** The group's people on the current page. */ rows?: PilotPerson[]; isAdmin: boolean; cols: Cols; reps: Rep[]; selection: Selection; onOpen: (id: string) => void }) {
  const [collapsed, setCollapsed] = useState(false);
  const [adding, setAdding] = useState(false);
  const remove = useRemovePilot();
  const groupLanguage = mostCommonLanguage(persons);
  // The group's language is said once in its header when it isn't the table's usual one.
  const languageNote = groupLanguage && groupLanguage !== 'nl_fr' && groupLanguage !== cols.usualLanguage ? LANGUAGE_META[groupLanguage].label : null;
  return (
    <Fragment>
      <GroupHeader
        title={company.companyName}
        ids={persons.map((p) => p.id)}
        isAdmin={isAdmin}
        colSpan={cols.span}
        selection={selection}
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        note={[company.country, languageNote, peopleCount(company.people), cols.other && company.unassigned > 0 ? `${company.unassigned} unassigned` : null].filter(Boolean).join(' · ')}
        extra={isAdmin && (
          <>
            <CompanyLanguage company={company} />
            {remove.error && <span className="text-body-xs text-error">Not removed: {remove.error.message}</span>}
            <MoreMenu items={[
              { label: 'Add people', onClick: () => setAdding(true) },
              { label: 'Remove company', danger: true, onClick: () => { if (window.confirm(`Remove ${company.companyName} and its ${peopleCount(company.people)} from this bucket? Nothing changes in lemlist.`)) remove.mutate(company.id); } },
            ]} />
          </>
        )}
      />
      {!collapsed && rows.map((person) => (
        <PersonRow key={person.id} entry={{ company, person }} groupLanguage={groupLanguage} isAdmin={isAdmin} cols={cols} view="company" selected={selection.has(person.id)} onSelect={(on) => selection.setMany([person.id], on)} onOpen={() => onOpen(person.id)} />
      ))}
      {isAdmin && adding && <AddPeopleModal company={company} isOpen={adding} onClose={() => setAdding(false)} reps={reps} />}
    </Fragment>
  );
}

function RepGroup({ rep, entries, rows = entries, isAdmin, cols, selection, onOpen }: { rep: Rep | null; entries: Entry[]; /** The group's people on the current page. */ rows?: Entry[]; isAdmin: boolean; cols: Cols; selection: Selection; onOpen: (id: string) => void }) {
  const [collapsed, setCollapsed] = useState(false);
  const companies = new Set(entries.map((e) => e.company.id)).size;
  return (
    <Fragment>
      <GroupHeader
        title={rep ? repLabel(rep) : 'Unassigned'}
        ids={entries.map((e) => e.person.id)}
        isAdmin={isAdmin}
        colSpan={cols.span}
        selection={selection}
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        note={`${peopleCount(entries.length)} · ${companies} ${companies === 1 ? 'company' : 'companies'}`}
      />
      {!collapsed && rows.map((entry) => (
        <PersonRow key={entry.person.id} entry={entry} isAdmin={isAdmin} cols={cols} view="rep" selected={selection.has(entry.person.id)} onSelect={(on) => selection.setMany([entry.person.id], on)} onOpen={() => onOpen(entry.person.id)} />
      ))}
    </Fragment>
  );
}

// ── Selection and bulk assignment ───────────────────────────────────────────

interface Selection { has: (id: string) => boolean; setMany: (ids: string[], on: boolean) => void; size: number; ids: string[]; clear: () => void }

function useSelection(): Selection {
  const [set, setSet] = useState<Set<string>>(new Set());
  return {
    has: (id) => set.has(id),
    size: set.size,
    ids: [...set],
    clear: () => setSet(new Set()),
    setMany: (ids, on) => setSet((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    }),
  };
}

/** Pinned to the bottom of the screen while anyone is selected, so it never scrolls away. */
function BulkBar({ bucketId, selection, reps, languageOf }: { bucketId: string; selection: Selection; reps: Rep[]; languageOf: Map<string, Language> }) {
  const assign = useAssignPeople(bucketId);
  const [rep, setRep] = useState('');
  const chosen = reps.find((r) => r.email === rep);
  // Said before the click, not only after: an SDR writes English only.
  const notEnglish = chosen?.role === 'sdr' ? selection.ids.filter((id) => (languageOf.get(id) ?? 'en') !== 'en').length : 0;
  const run = (assigneeEmail: string | null) => assign.mutate({ personIds: selection.ids, assigneeEmail }, { onSuccess: () => { selection.clear(); setRep(''); } });
  return (
    <div className="fixed inset-x-0 bottom-4 z-30 flex justify-center px-4">
      <div className="flex max-w-3xl flex-wrap items-center gap-3 rounded-xl border border-border-light bg-surface-primary px-4 py-2.5 shadow-medium">
        <span className="text-body-sm font-medium text-text-primary">{selection.size} selected</span>
        <div className="w-48"><RepSelect reps={reps} value={rep} placeholder="Give them to…" onChange={setRep} /></div>
        <Button size="sm" isLoading={assign.isPending} disabled={!rep} onClick={() => run(rep)}>Assign</Button>
        <MoreMenu items={[{ label: 'Unassign', onClick: () => run(null) }, { label: 'Clear selection', onClick: selection.clear }]} />
        {notEnglish > 0 && <span className="basis-full text-body-xs text-warning-dark">{notEnglish} need another language than English, and {repName(chosen ?? null)} is an SDR.</span>}
        {assign.error && <span className="basis-full text-body-xs text-error">Not saved: {assign.error.message}</span>}
      </div>
    </div>
  );
}

// ── The table ───────────────────────────────────────────────────────────────

type View = 'company' | 'rep';
/** all | unassigned | flagged | a rep's email. */
type Show = string;


/** "⚠ 342 of 354 imported 8+ days ago, in no lemlist campaign": the list-wide flags, said once with their counts. */
function commonFlagsText(entries: Entry[], common: ReadonlySet<Flag['kind']>): string | null {
  if (common.size === 0) return null;
  if (common.size > 1) return `⚠ ${common.size} flags on most rows`;
  const kind = [...common][0]!;
  const flags = entries.flatMap((e) => e.person.flags.filter((f) => f.kind === kind));
  const first = flags[0]!;
  const count = entries.length === flags.length ? 'all' : `${flags.length} of ${entries.length}`;
  if (first.kind === 'not_started') {
    const days = Math.min(...flags.map((f) => (f.kind === 'not_started' ? f.days : Infinity)));
    return `⚠ ${count} imported ${days}+ days ago, in no lemlist campaign`;
  }
  return `⚠ ${count}: ${flagText(first)}`;
}

export function PeopleTable({ bucketId, companies: allCompanies, isAdmin, launched }: { bucketId: string; companies: PilotDetail[]; isAdmin: boolean; launched: boolean }) {
  // A bucket that has not launched has nobody "in no lemlist campaign" yet: that is the plan, not a fault.
  const companies = useMemo(
    () => launched
      ? allCompanies
      : allCompanies.map((c) => ({ ...c, persons: c.persons.map((p) => ({ ...p, flags: p.flags.filter((f) => f.kind !== 'not_started') })) })),
    [allCompanies, launched],
  );
  const { user } = useAuth();
  const reps = useReps();
  // Seats set to "Not a rep" in Team roles never show as a choice or a group.
  const repList = useMemo(() => (reps.data?.reps ?? []).filter((r) => !r.off).sort((a, b) => repName(a).localeCompare(repName(b))), [reps.data]);
  const me = user?.email.toLowerCase() ?? '';
  const [view, setView] = useState<View>('company');
  const [language, setLanguage] = useState<Language | 'all'>('all');
  // Reps land on their own people; admins on everyone. Chosen once, when the rep list is known.
  const [show, setShow] = useState<Show | null>(null);
  const shown: Show = show ?? (!isAdmin && repList.some((r) => r.email.toLowerCase() === me) ? me : 'all');
  const [adding, setAdding] = useState<'company' | 'import' | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const selection = useSelection();
  // A new filter starts a new selection, so nobody hidden can be assigned by mistake.
  const filter = <T,>(set: (v: T) => void) => (v: T) => { selection.clear(); set(v); };

  const allEntries: Entry[] = useMemo(() => companies.flatMap((company) => company.persons.map((person) => ({ company, person }))), [companies]);
  const keep = useMemo(() => (p: PilotPerson) =>
    (language === 'all' || p.language === language)
    && (shown === 'all'
      || (shown === 'unassigned' && !p.assignee)
      || (shown === 'flagged' && p.flags.length > 0)
      || p.assignee?.email.toLowerCase() === shown), [language, shown]);
  const entries = useMemo(() => allEntries.filter((e) => keep(e.person)), [allEntries, keep]);
  const filtered = language !== 'all' || shown !== 'all';
  // People-level only: a company shows once someone at it is in the list, A to Z.
  const shownCompanies = useMemo(
    () => companies
      .map((c) => ({ company: c, persons: c.persons.filter(keep) }))
      .filter((g) => g.persons.length > 0)
      .sort((a, b) => a.company.companyName.localeCompare(b.company.companyName)),
    [companies, keep],
  );
  const languageOf = useMemo(() => new Map(allEntries.map((e) => [e.person.id, e.person.language])), [allEntries]);
  const count = (pred: (p: PilotPerson) => boolean) => allEntries.filter((e) => pred(e.person)).length;
  const languages = LANGUAGES.filter((l) => allEntries.some((e) => e.person.language === l));

  // Unassigned first, then reps by name; the rep list comes from lemlist so a rep with nobody still shows up when nothing is filtered.
  const repGroups = useMemo(() => {
    const groups = new Map<string, { rep: Rep | null; entries: Entry[] }>();
    groups.set('', { rep: null, entries: [] });
    for (const r of repList) groups.set(r.email, { rep: r, entries: [] });
    for (const e of entries) {
      const key = e.person.assignee?.email ?? '';
      const g = groups.get(key) ?? { rep: e.person.assignee, entries: [] };
      g.entries.push(e);
      groups.set(key, g);
    }
    return [...groups.values()].filter((g) => g.entries.length > 0 || (g.rep !== null && !filtered));
  }, [entries, repList, filtered]);

  // A column with one value for everyone shown (all unassigned, all not started) is said once above the table.
  // By rep, the other column is the company, which varies, and the rep is already the group.
  const repSame = view === 'company' && isConstant(entries, (e) => e.person.assignee?.email ?? null);
  // Stage goes when it reads the same for everyone, including when nobody has started (every cell would be blank).
  const allNotStarted = entries.length > 0 && entries.every((e) => e.person.stage === 'not_started');
  const stageSame = allNotStarted || isConstant(entries, (e) => e.person.stage);
  // Last touch only means something once the bucket has launched.
  const anyTouch = launched && entries.some((e) => e.person.lastTouchAt);
  // A flag that (nearly) every row carries is a property of the list, not of a person: said once above the
  // table with its count, and no row gets a ⚠ for it. Only the exceptions are marked (342 of 354 not started).
  const commonFlags = useMemo(() => {
    const n = entries.length;
    if (n < 2) return new Set<Flag['kind']>();
    const counts = new Map<Flag['kind'], number>();
    for (const e of entries) for (const k of new Set(e.person.flags.map((f) => f.kind))) counts.set(k, (counts.get(k) ?? 0) + 1);
    return new Set([...counts].filter(([, c]) => c === n || (n >= 10 && c / n >= 0.8)).map(([k]) => k));
  }, [entries]);
  const anyOwnFlag = entries.some((e) => e.person.flags.some((f) => !commonFlags.has(f.kind)));
  const usualLanguage = useMemo(() => mostCommonLanguage(entries.map((e) => e.person)), [entries]);
  const languageSame = isConstant(entries, (e) => e.person.language);
  const cols: Cols = {
    other: !repSame,
    stage: false,
    lastTouch: anyTouch,
    commonFlags,
    flags: anyOwnFlag,
    usualLanguage: entries.length > 1 ? usualLanguage : null,
    span: (isAdmin ? 1 : 0) + 1 + (anyTouch ? 1 : 0) + (repSame ? 0 : 1) + (anyOwnFlag ? 1 : 0),
  };
  const commonFlagNote = commonFlagsText(entries, commonFlags);
  const sameNote = [
    repSame && entries[0] && (entries[0].person.assignee ? `all with ${repName(entries[0].person.assignee)}` : 'all unassigned'),
    allNotStarted ? 'Not started' : stageSame && entries[0] && `all ${STAGE_META[entries[0].person.stage].label.toLowerCase()}`,
    launched && !anyTouch && !allNotStarted && entries.length > 0 && 'no touches yet',
    !stageSame && entries.length > 1 && 'no stage badge = not started',
    cols.usualLanguage &&
      cols.usualLanguage !== 'nl_fr' &&
      (languageSame ? `all ${LANGUAGE_META[cols.usualLanguage].label}` : `${LANGUAGE_META[cols.usualLanguage].label} unless marked`),
    commonFlagNote,
  ].filter(Boolean).join(' · ');
  // Fixed widths keep Stage next to Person; the spare room goes to the last column.
  const colWidths = [
    ...(isAdmin ? ['2.5rem'] : []),
    '20rem',
    ...(cols.other ? ['12rem'] : []),
    ...(cols.lastTouch ? ['8rem'] : []),
    ...(cols.flags ? ['3rem'] : []),
  ];
  colWidths[colWidths.length - 1] = 'auto';

  // 15 people a page, in the order the groups list them. A group split across pages repeats its header,
  // which still counts and selects the whole group.
  const ordered = useMemo(
    () => view === 'company'
      ? shownCompanies.flatMap((g) => g.persons.map((person) => ({ company: g.company, person })))
      : repGroups.flatMap((g) => g.entries),
    [view, shownCompanies, repGroups],
  );
  const { pageItems, ...paging } = usePagination(ordered);
  const onPage = useMemo(() => new Set(pageItems.map((e) => e.person.id)), [pageItems]);
  const isLastPage = paging.page === paging.pageCount;

  const visibleIds = entries.map((e) => e.person.id);
  const allOn = visibleIds.length > 0 && visibleIds.every((id) => selection.has(id));
  const openEntry = openId ? allEntries.find((e) => e.person.id === openId) ?? null : null;

  return (
    <Card padding="none">
      <div className="space-y-3 p-4">
        {reps.error && <Alert variant="error">Could not load the rep list from lemlist: {reps.error.message}</Alert>}
        <div className="flex flex-wrap items-center gap-2">
          <select className={selectClass} value={view} onChange={(e) => setView(e.target.value as View)} aria-label="Group by">
            <option value="company">By company</option>
            <option value="rep">By rep</option>
          </select>
          <select className={selectClass} value={language} onChange={(e) => filter(setLanguage)(e.target.value as Language | 'all')} aria-label="Language">
            <option value="all">All languages</option>
            {languages.map((l) => <option key={l} value={l}>{LANGUAGE_META[l].label} · {count((p) => p.language === l)}</option>)}
          </select>
          <select className={selectClass} value={shown} onChange={(e) => filter(setShow)(e.target.value)} aria-label="Show">
            <option value="all">Everyone · {allEntries.length}</option>
            {/* An option that lists the same people as Everyone (all unassigned) says nothing: left out. */}
            {count((p) => !p.assignee) > 0 && count((p) => !p.assignee) < allEntries.length && (
              <option value="unassigned">Unassigned · {count((p) => !p.assignee)}</option>
            )}
            {count((p) => p.flags.length > 0) > 0 && count((p) => p.flags.length > 0) < allEntries.length && (
              <option value="flagged">Flagged · {count((p) => p.flags.length > 0)}</option>
            )}
            <optgroup label="Rep">
              {repList.filter((r) => count((p) => p.assignee?.email === r.email) > 0 || r.email.toLowerCase() === shown).map((r) => <option key={r.email} value={r.email.toLowerCase()}>{r.email.toLowerCase() === me ? 'My people' : repName(r)} · {count((p) => p.assignee?.email === r.email)}</option>)}
            </optgroup>
          </select>
          {(filtered || sameNote) && (
            <span className="text-body-xs text-text-tertiary">
              {filtered ? `${entries.length} shown` : peopleCount(entries.length)}
              {sameNote && ` · ${sameNote}`}
            </span>
          )}
          {isAdmin && (
            <div className="ml-auto">
              <MoreMenu label="Add" items={[{ label: 'Import list', onClick: () => setAdding('import') }, { label: 'Add company', onClick: () => setAdding('company') }]} />
            </div>
          )}
        </div>

        {allEntries.length === 0 ? (
          <p className="py-6 text-center text-body-sm text-text-tertiary">No people yet.{isAdmin && ' Add → Import list.'}</p>
        ) : (
          <Table minWidth={760} colWidths={colWidths}>
            <THead>
              <tr>
                {isAdmin && <Th><input type="checkbox" checked={allOn} disabled={visibleIds.length === 0} onChange={(e) => selection.setMany(visibleIds, e.target.checked)} aria-label="Select everyone shown" /></Th>}
                <Th>Person</Th>
                {cols.other && <Th>{view === 'rep' ? 'Company' : 'Rep'}</Th>}
                {cols.lastTouch && <Th>Last touch</Th>}
                {cols.flags && (
                  <Th title="Flags: something to check on this person (hover the ⚠ for what)">
                    <span aria-label="Flags">⚠</span>
                  </Th>
                )}
              </tr>
            </THead>
            <tbody>
              {view === 'company'
                ? shownCompanies
                    .filter((g) => g.persons.some((p) => onPage.has(p.id)))
                    .map(({ company: c, persons }) => <CompanyGroup key={c.id} company={c} persons={persons} rows={persons.filter((p) => onPage.has(p.id))} isAdmin={isAdmin} cols={cols} reps={repList} selection={selection} onOpen={setOpenId} />)
                : repGroups
                    // A rep with nobody yet is listed once, on the last page.
                    .filter((g) => (g.entries.length === 0 ? isLastPage : g.entries.some((e) => onPage.has(e.person.id))))
                    .map((g) => <RepGroup key={g.rep?.email ?? ''} rep={g.rep} entries={g.entries} rows={g.entries.filter((e) => onPage.has(e.person.id))} isAdmin={isAdmin} cols={cols} selection={selection} onOpen={setOpenId} />)}
            </tbody>
          </Table>
        )}
        {allEntries.length > 0 && <Pagination {...paging} onPage={paging.setPage} />}
        {/* Room for the pinned bar, so it never covers the last rows. */}
        {isAdmin && selection.size > 0 && <div className="h-16" />}
      </div>
      {isAdmin && selection.size > 0 && <BulkBar bucketId={bucketId} selection={selection} reps={repList} languageOf={languageOf} />}
      {openEntry && <PersonPanel entry={openEntry} isAdmin={isAdmin} reps={repList} onClose={() => setOpenId(null)} />}
      {isAdmin && <AddCompanyModal bucketId={bucketId} isOpen={adding === 'company'} onClose={() => setAdding(null)} />}
      {isAdmin && adding === 'import' && <ImportListModal bucketId={bucketId} isOpen onClose={() => setAdding(null)} reps={repList} />}
    </Card>
  );
}
